/**
 * 사용자 주석 — 편집기 글에서 값과 그 값이 걸린 컬럼을 읽는 순수 함수(docs/features/32-user-comment.md).
 */

/** 'A01' → A01, '' 안의 '' 는 ' 하나. 따옴표가 없으면 그대로(앞뒤 공백만 뗀다) */
export function unquoteValue(text) {
    const s = String(text == null ? '' : text).trim();
    if (s.length >= 2 && s[0] === "'" && s[s.length - 1] === "'") return s.slice(1, -1).replace(/''/g, "'");
    if (s.length >= 2 && s[0] === '"' && s[s.length - 1] === '"') return s.slice(1, -1);
    return s;
}

/**
 * 값 바로 앞의 컬럼 — `a.CUST_STAT_CD = '01'` · `STATUS IN ('A', ` · `x <> 3` 의 컬럼 이름(별칭은 뗀다).
 * 못 찾으면 null — 그때는 모든 영역에서 찾는다.
 */
export function columnBeforeValue(textBefore) {
    const s = String(textBefore || '').slice(-400);
    const re = /([A-Za-z0-9_$#\u3131-\uD79D."]+)\s*(?:=|<>|!=|\^=|>=|<=|>|<|\bnot\s+in\s*\([^()]*|\bin\s*\([^()]*|\bnot\s+like|\blike)\s*$/i;
    const m = s.match(re);
    if (!m) return null;
    const parts = m[1].split('.').filter(Boolean);
    const last = parts.length ? parts[parts.length - 1].replace(/"/g, '') : '';
    return last || null;
}

// ── F2 의 이름(테이블·컬럼) — 2026-11-04 ─────────────────────────────

const NAME_CH = 'A-Za-z0-9_$#\u3131-\uD79D';
const NAME_ONLY = new RegExp(`^"?[${NAME_CH}]+"?$`);
const STOP = /^(WHERE|GROUP|ORDER|HAVING|CONNECT|START|UNION|INTERSECT|MINUS|EXCEPT|ON|USING|LEFT|RIGHT|INNER|OUTER|FULL|CROSS|NATURAL|JOIN|SET|VALUES|SELECT|FETCH|LIMIT|OFFSET|FOR|WINDOW|PARTITION|MODEL|PIVOT|UNPIVOT|AS)$/i;

/** 고른 글이 이름 하나 모양인가(숫자만이면 아니다 — 값이다) */
export function isNameLike(text) {
    const s = String(text || '').trim();
    return NAME_ONLY.test(s) && !/^\d+(\.\d+)?$/.test(s.replace(/"/g, ''));
}

const bare = (s) => {
    const parts = String(s || '').split('.').filter(Boolean);
    return (parts.length ? parts[parts.length - 1] : '').replace(/"/g, '');
};

/**
 * SQL 글의 FROM · JOIN · UPDATE · INTO 뒤 테이블과 별칭 — [{ table, alias }] (스키마는 뗀다, 대문자).
 * 글자 해석이라 완전하지 않다 — F2 가 컬럼 주석을 고를 때 그 SQL 의 테이블을 앞세우는 데만 쓴다.
 */
export function tablesInSql(text) {
    const s = String(text || '').replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/'(?:[^']|'')*'/g, "''");
    const out = []; const seen = new Set();
    const add = (tbl, alias) => {
        const t = bare(tbl).toUpperCase();
        if (!t || STOP.test(t) || /^\d/.test(t)) return;
        const a = alias && !STOP.test(alias) ? alias.replace(/"/g, '').toUpperCase() : null;
        const k = `${t}|${a || ''}`;
        if (seen.has(k)) return;
        seen.add(k);
        out.push({ table: t, alias: a });
    };
    const item = new RegExp(String.raw`^\s*([${NAME_CH}."]+)(?:\s+(?:AS\s+)?("?[${NAME_CH}]+"?))?`, 'i');
    // FROM 목록 — 쉼표로 나뉜 테이블마다(괄호 안 부질의는 건너뛴다)
    const fromRe = /\bFROM\b([\s\S]*?)(?=\bWHERE\b|\bGROUP\b|\bORDER\b|\bHAVING\b|\bCONNECT\b|\bSTART\b|\bUNION\b|\bINTERSECT\b|\bMINUS\b|\bEXCEPT\b|\bJOIN\b|\bFETCH\b|\bLIMIT\b|[;)]|$)/gi;
    let m;
    while ((m = fromRe.exec(s))) {
        m[1].split(',').forEach(part => { const x = part.match(item); if (x && !part.trim().startsWith('(')) add(x[1], x[2]); });
        if (m[0].length === 0) fromRe.lastIndex += 1;
    }
    const kwRe = new RegExp(String.raw`\b(?:JOIN|UPDATE|INTO)\s+([${NAME_CH}."]+)(?:\s+(?:AS\s+)?("?[${NAME_CH}]+"?))?`, 'gi');
    while ((m = kwRe.exec(s))) add(m[1], m[2]);
    return out;
}

/** 커서 이름 바로 앞의 한정자 — `e.ENAME` 의 `E`(대문자, 따옴표 뗌). 없으면 null */
export function qualifierBefore(textBefore) {
    const m = String(textBefore || '').match(new RegExp(String.raw`("?[${NAME_CH}]+"?)\.\s*$`));
    return m ? m[1].replace(/"/g, '').toUpperCase() : null;
}

/** 한정자(별칭 또는 테이블 이름)가 가리키는 테이블 — 못 정하면 null */
export function tableForQualifier(tables, qualifier) {
    if (!qualifier) return null;
    const q = String(qualifier).toUpperCase();
    const hit = (tables || []).find(x => x.alias === q) || (tables || []).find(x => x.table === q);
    return hit ? hit.table : null;
}
